/**
 * Bank name rules — act on elements by what their title says.
 * For Media Devoted | Created By Ciro Autiero
 *
 * A rule reads the titles in ONE bank and parks the PATHS that use a matching
 * element. Networks stamp status into offer titles ("[PAUSED - CAP]") and
 * clients supply banned words; either way the paths serving them should stop.
 */

/** Which bank's titles a rule reads. */
export type BankRuleResource = 'paths' | 'landers' | 'offers';

/** Always applied case-insensitively — the same word arrives cased every way. */
export type BankRuleOperator =
  | 'contains'
  | 'not_contains'
  | 'equals'
  | 'not_equals';

/**
 * How many of a path's children must match before the path is parked.
 *
 * Only meaningful for the offers/landers banks. Real data is why this is a
 * choice rather than a constant: a "paused" rule over 592 live paths finds 463
 * where EVERY offer is paused (genuinely dead) and 129 where a paused offer
 * sits beside working ones — `ALL` spares those, `ANY` does not. A banned-word
 * rule wants the opposite.
 */
export type BankRuleQuorum = 'ANY' | 'ALL';

/** What happens to the owning paths. Only parking today. */
export type BankRuleAction = 'park';

export interface BankNameRule {
  id: string;
  resource: BankRuleResource;
  /** The rule's own label, e.g. "Network paused offers". */
  name: string;
  operator: BankRuleOperator;
  /** A title matches when ANY term satisfies the operator. */
  terms: string[];
  /** Ignored for the `paths` resource — a path matches on its own name. */
  quorum: BankRuleQuorum;
  action: BankRuleAction;
  /**
   * Most paths one check may take out; null = no limit. Explicit, because a cap
   * nobody can see is a cap nobody can reason about.
   */
  maxPerRun?: number | null;
  /** False until someone reads the preview and arms it. */
  enabled: boolean;
  orderIndex: number;
  lastRunAt?: string | null;
  lastParkedCount?: number | null;
  /** Why the last run declined to move something — shown on the rule row so an
   *  armed rule that has quietly stopped is not mistaken for an idle one. */
  lastSkippedReason?: string | null;
  /** Paths matched on the last check — the surge guard's baseline. */
  lastMatchCount?: number | null;
  /**
   * True while a check is in flight. A run over hundreds of paths is minutes of
   * tracker calls, which looks identical to nothing happening unless it is said.
   */
  running?: boolean;
  runStartedAt?: string | null;
  /** Paths moved so far in the run currently in flight. */
  runMoved?: number | null;
  createdAt: string;
  updatedAt: string;
}

/** What a rule WOULD do right now — the preview that gates arming. */
export interface BankNameRulePreview {
  /** Rows in the rule's own bank whose title matches. */
  matchedElements: number;
  /**
   * Paths that USE a matching element, BEFORE the quorum narrows them. With
   * "all must match" this is far larger than the number that qualifies, and
   * without it the drop from matched offers to affected paths looks arbitrary.
   */
  candidatePaths?: number;
  /** Distinct paths that would actually MOVE — after the quorum AND the guards. */
  matchedPaths: number;
  /**
   * How many of those are live on a tracker. This is the number that matters:
   * parking a live path is a real tracker write that stops real traffic.
   */
  livePaths: number;
  /**
   * Everything eligible to move, before this rule's per-check limit is applied.
   * `matchedPaths` is what THIS check would take; the difference follows on
   * later checks.
   */
  eligibleTotal?: number;
  /** True when the run would be stopped by the per-run cap. */
  overCap: boolean;
  /** A sample of what would be hit, for the "show them" list. */
  sample: BankNameRulePreviewRow[];
  /**
   * Matched paths the guards would refuse, with the reason — the last live path
   * on its flow, a campaign whose pushes are paused. Shown so the preview never
   * promises a move that the run will silently decline.
   */
  blocked?: BankNameRuleBlockedRow[];
}

/** One path this rule moved to Waiting, for the history panel. */
export interface BankNameRuleHistoryEntry {
  id: string;
  pathBankId: string;
  pathName: string;
  flowId: string | null;
  at: string;
  reason?: string | null;
  /** False ⇒ the path is live again (undone, or re-promoted since). */
  stillWaiting: boolean;
}

/** A matching path the last check did NOT move, and why. */
export interface BankNameRuleNotMoved {
  id: string;
  pathBankId: string;
  pathName: string;
  /** "kept" = a guard protected it · "failed" = the tracker refused the write. */
  status: "kept" | "failed";
  code?: BlockReasonCode | null;
  reason: string;
  at: string;
}

/** A page of history plus the counts that page cannot know. */
export interface BankNameRuleHistoryResult {
  entries: BankNameRuleHistoryEntry[];
  /** Matching the current filter — so the UI can say "showing 20 of 412". */
  total: number;
  /** Oldest and newest move ever, ignoring the filter, for the summary line. */
  firstMoveAt: string | null;
  lastMoveAt: string | null;
  /**
   * Paths the LAST check matched but did not move. Successful moves are a
   * timeline; these are a snapshot of the current standoff, which is what
   * answers "418 match but are not in Waiting — why?".
   */
  notMoved: BankNameRuleNotMoved[];
}

/** Preset windows offered by the history panel. */
export const HISTORY_RANGES = [
  { value: "24h", label: "24 hours", hours: 24 },
  { value: "7d", label: "7 days", hours: 24 * 7 },
  { value: "30d", label: "30 days", hours: 24 * 30 },
  { value: "all", label: "All time", hours: 0 },
] as const;

export type HistoryRange = (typeof HISTORY_RANGES)[number]["value"];

export const HISTORY_PAGE_SIZE = 25;

export interface BankNameRuleBlockedRow {
  pathBankId: string;
  pathName: string;
  /** The full sentence, for the row's second line. */
  reason: string;
  /** Short code so the list can chip it without parsing prose. */
  code?: BlockReasonCode;
}

export type BlockReasonCode =
  | "only-path"
  | "flow-unreadable"
  | "campaign-paused"
  | "no-ids";

/** Short chip label per reason — the sentence stays underneath. */
export const BLOCK_REASON_LABEL: Record<BlockReasonCode, string> = {
  "only-path": "Only path on its flow",
  "flow-unreadable": "Tracker did not answer",
  "campaign-paused": "Pushes paused",
  "no-ids": "No tracker id",
};

export interface BankNameRulePreviewRow {
  pathBankId: string;
  pathName: string;
  /** The child title that triggered it — empty for a paths-bank rule. */
  matchedBy?: string;
  live: boolean;
}

/**
 * No single run may park more than this. A term that was narrow yesterday can
 * be wide today once a pull adds offers, and this is the difference between a
 * surprising afternoon and an outage.
 */
export const BANK_RULE_MAX_PARKS_PER_RUN = 200;

/** What a new rule starts with — a limit, but one the author can see and change. */
export const BANK_RULE_DEFAULT_MAX_PER_RUN = 200;

/** Guard rail on the input itself, not a hidden behaviour. */
export const BANK_RULE_MAX_PER_RUN_LIMIT = 5000;

export const BANK_RULE_OPERATORS: ReadonlyArray<{
  value: BankRuleOperator;
  label: string;
}> = [
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: "doesn't contain" },
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: "doesn't equal" },
];
